import { Button, Card, Flex, List, Typography } from 'antd';
import { ReactNode } from 'react';
import { PreSalesAvailabilityResult, PreSalesSubmitFormResponse, Slot } from '../../../dto/model';
import { strings } from '../../../lang';
import { PreSalesBookingSuccess } from './pre-sales-booking-success';
import {
  PreSalesBookingTips,
  PreSalesCustomerPortalTip,
} from './pre-sales-booking-tips';
import { PreSalesQuestionnaireCard } from './pre-sales-questionnaire-card';

type PreSalesQuestionnaireResultProps = {
  submissionResult: PreSalesSubmitFormResponse;
  footerActions?: ReactNode;
  bookedFieldSalesSlot: Slot | null;
  hasConfirmedAgentOnlyFieldSales: boolean;
  bookedVideoSlot: Slot | null;
  fieldSalesAvailabilityChecked: boolean;
  fieldSalesQuestionVisible: boolean;
  fieldSalesChoice: boolean | null;
  fieldSalesAvailability: PreSalesAvailabilityResult | null;
  isConfirmingAgentOnlyFieldSales: boolean;
  fieldSalesCalendar: ReactNode;
  videoCalendar: ReactNode;
  showCustomerPortalTip: boolean;
  onChooseFieldSales: () => void;
  onChooseVideoSales: () => void;
  onBackToForm: () => void;
};

export const PreSalesQuestionnaireResult = ({
  submissionResult,
  footerActions,
  bookedFieldSalesSlot,
  hasConfirmedAgentOnlyFieldSales,
  bookedVideoSlot,
  fieldSalesAvailabilityChecked,
  fieldSalesQuestionVisible,
  fieldSalesChoice,
  fieldSalesAvailability,
  isConfirmingAgentOnlyFieldSales,
  fieldSalesCalendar,
  videoCalendar,
  showCustomerPortalTip,
  onChooseFieldSales,
  onChooseVideoSales,
  onBackToForm,
}: PreSalesQuestionnaireResultProps) => {
  const renderResult = (content: ReactNode) => (
    <PreSalesQuestionnaireCard
      isResult
      footer={(
        <>
          <Button onClick={onBackToForm}>
            {strings.preSalesQuestionnaire.backToFormButton}
          </Button>
          {footerActions}
        </>
      )}
    >
      {content}
    </PreSalesQuestionnaireCard>
  );

  if (bookedFieldSalesSlot) {
    return renderResult(
      <>
        <PreSalesBookingTips showCustomerPortal={false} />
        <PreSalesBookingSuccess
          bookedSlot={bookedFieldSalesSlot}
          title={strings.preSalesQuestionnaire.fieldSalesBookingSuccessTitle}
        />
      </>,
    );
  }

  if (hasConfirmedAgentOnlyFieldSales) {
    return renderResult(
      <>
        <PreSalesBookingTips showCustomerPortal={false} />
        <Card
          size="small"
          title={(
            <Typography.Title level={4} style={{ margin: 0 }}>
              {strings.preSalesQuestionnaire.fieldSalesBookingSuccessTitle}
            </Typography.Title>
          )}
        >
          {fieldSalesAvailability?.agent?.seller && (
            <Typography.Text>
              {strings.salesManager}:{' '}
              <Typography.Text strong>
                {fieldSalesAvailability.agent.seller}
              </Typography.Text>
            </Typography.Text>
          )}
        </Card>
      </>,
    );
  }

  if (bookedVideoSlot) {
    return renderResult(
      <>
        <PreSalesBookingTips showCustomerPortal={showCustomerPortalTip} />
        <PreSalesBookingSuccess
          bookedSlot={bookedVideoSlot}
          title={strings.preSalesQuestionnaire.videoBookingSuccessTitle}
        />
      </>,
    );
  }

  if (!submissionResult.buildable) {
    const reasonGroups = Object.entries(submissionResult.cancellationReasons ?? {}).filter(
      ([, reasons]) => reasons.length > 0,
    );

    return renderResult(
      <Card
        size="small"
        title={(
          <Typography.Title level={4} type="danger" style={{ margin: 0 }}>
            {strings.preSalesQuestionnaire.resultCancellationTitle}
          </Typography.Title>
        )}
      >
        {reasonGroups.length > 0 ? (
          <Flex vertical gap={12}>
            <Typography.Paragraph>
              {strings.preSalesQuestionnaire.resultCancellationDescription}
            </Typography.Paragraph>
            <List
              split={false}
              dataSource={reasonGroups}
              renderItem={([product, reasons]) => (
                <List.Item>
                  <Flex vertical gap={4} flex={1}>
                    <Typography.Text strong>{product}</Typography.Text>
                    <List
                      size="small"
                      dataSource={reasons}
                      renderItem={(reason) => (
                        <List.Item>
                          <Typography.Text>{reason}</Typography.Text>
                        </List.Item>
                      )}
                    />
                  </Flex>
                </List.Item>
              )}
            />
          </Flex>
        ) : (
          <Typography.Paragraph>
            {strings.preSalesQuestionnaire.resultCancellationNoReasons}
          </Typography.Paragraph>
        )}
      </Card>,
    );
  }

  if (!fieldSalesAvailabilityChecked) {
    return renderResult(
      <>
        <PreSalesBookingTips showCustomerPortal={false} />
        <Card
          size="small"
          title={(
            <Typography.Title level={4} style={{ margin: 0 }}>
              {strings.preSalesQuestionnaire.resultBuildableTitle}
            </Typography.Title>
          )}
        >
          <Typography.Paragraph>
            {strings.preSalesQuestionnaire.fieldSalesAvailabilityChecking}
          </Typography.Paragraph>
        </Card>
      </>,
    );
  }

  if (fieldSalesQuestionVisible) {
    return renderResult(
      <>
        <PreSalesBookingTips showCustomerPortal={false} />
        <Card
          size="small"
          title={(
            <Typography.Title level={4} style={{ margin: 0 }}>
              {strings.preSalesQuestionnaire.resultBuildableTitle}
            </Typography.Title>
          )}
        >
          <Flex vertical gap={12}>
            <Typography.Paragraph>
              {strings.preSalesQuestionnaire.fieldSalesChoiceQuestion}
            </Typography.Paragraph>
            <Flex gap={8} wrap>
              <Button
                type={fieldSalesChoice === true ? 'primary' : 'default'}
                loading={isConfirmingAgentOnlyFieldSales}
                disabled={isConfirmingAgentOnlyFieldSales}
                onClick={onChooseFieldSales}
              >
                {strings.preSalesQuestionnaire.yesButton}
              </Button>
              <Button
                type={fieldSalesChoice === false ? 'primary' : 'default'}
                disabled={isConfirmingAgentOnlyFieldSales}
                onClick={onChooseVideoSales}
              >
                {strings.preSalesQuestionnaire.noButton}
              </Button>
            </Flex>
          </Flex>
        </Card>
        {fieldSalesChoice === true && fieldSalesAvailability?.bookableAgent && fieldSalesCalendar}
        {fieldSalesChoice === false && (
          <>
            {showCustomerPortalTip && <PreSalesCustomerPortalTip />}
            {videoCalendar}
          </>
        )}
      </>,
    );
  }

  return renderResult(
    <>
      <PreSalesBookingTips showCustomerPortal={showCustomerPortalTip} />
      <Typography.Title level={4} style={{ margin: 0 }}>
        {strings.preSalesQuestionnaire.resultBuildableTitle}
      </Typography.Title>
      {videoCalendar}
    </>,
  );
};
